import React, { useState, useEffect } from "react";
import { Modal, Button, Pagination } from "antd";
import { List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import axiosInstance from "../../Config/axiosConfig";
import { useNavigate } from "react-router-dom";
import "./CreatedVideos.css";
import ClassOverview from "../ClassOverview/ClassOverview";
import CreatedVideosLoading from "../CreatedVideosLoading/CreatedVideosLoading";

const CreatedVideos = ({ userId }) => {
  const [videos, setVideos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("draft");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();

  const fetchClasses = async (status = 1, page = 1) => {
    setLoading(true);

    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem("deviceId");
    const storedUserIp = localStorage.getItem("userIp");

    const config = {
      method: "get",
      url: `/class/get-creator-classes/${status}/${page}`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        UserDevice: storedDeviceId,
        UserIPAddress: storedUserIp,
      },
    };

    try {
      const response = await axiosInstance(config);
      if (response.data && Array.isArray(response.data.data.classes)) {
        setVideos(response.data.data.classes);
        setTotalPages(response.data.data.pages || 1);
      } else {
        setVideos([]);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching classes:", error.response || error);
      setVideos([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const statusMap = { draft: 1, pending: 2, published: 3, rejected: 4 };
    const statusValue = statusMap[selectedStatus] || 1;
    fetchClasses(statusValue, currentPage);
  }, [selectedStatus, currentPage]);

  const handleDelete = async () => {
    if (!videoToDelete) return;

    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem("deviceId");
    const storedUserIp = localStorage.getItem("userIp");

    console.log("Deleting class with ID:", videoToDelete.id);

    try {
      const response = await axiosInstance.get(
        `/class/delete/${videoToDelete.id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
          },
        }
      );

      console.log("Delete API Response:", response.data);
   
      const updatedVideos = videos.filter(
        (video) => video.id !== videoToDelete.id
      );
      setVideos(updatedVideos);
      setShowModal(false);
      setVideoToDelete(null);
    } catch (error) {
      console.error("Error deleting class:", error.response?.data || error.message || error);
    }
  };

  const confirmDelete = (video) => {
    console.log("Confirming deletion for video:", video);
    setVideoToDelete(video);
    setShowModal(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const menuItems = [
    { key: "draft", label: "Draft Classes" },
    { key: "pending", label: "Pending Classes" },
    { key: "published", label: "Published Classes" },
    { key: "rejected", label: "Rejected Classes" },
  ];

  return (
    <div className="created-videos-container">
      {loading ? (
        <CreatedVideosLoading />
      ) : (
        <>
          {/* Sidebar */}
          <div className="created-videos-sidebar">
            <List>
              {menuItems.map((item) => (
                <ListItem
                  key={item.key}
                  disablePadding
                  onClick={() => setSelectedStatus(item.key)}
                  sx={{
                    borderRadius: "4px",
                    marginBottom: "8px",
                    backgroundColor:
                      selectedStatus === item.key ? "#82ff5d" : "transparent",
                    color: selectedStatus === item.key ? "#ffffff" : "inherit",
                    "&:hover": {
                      backgroundColor: "#e6f7ff",
                    },
                  }}
                >
                  <ListItemButton>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </div>

          {/* Main Content */}
          <div className="main-content">
            {videos.length > 0 ? (
              <>
                {videos.map((video) => {
                  const showEditButton =
                    video.status === "draft" || video.status === "rejected";
                  const showDeleteButton =
                    video.status === "draft" ||
                    video.status === "pending" ||
                    video.status === "rejected";

                  return (
                    <div key={video.id} className="created-videos">
                      <ClassOverview
                        video={video}
                        classId={video.id}
                        onEdit={() =>
                          navigate(`/teacher-createClass/${video.id}`, {
                            state: {
                              teacherId: userId,
                            },
                          })
                        }
                        onDelete={() => confirmDelete(video)} // ✅ pass full object
                        showEditButton={showEditButton}
                        showDeleteButton={showDeleteButton}
                      />
                      <div className="created-video-bottom-line"></div>
                    </div>
                  );
                })}

                {/* Pagination */}
                <div className="pagination-container">
                  <Pagination
                    current={currentPage}
                    total={totalPages * 10}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                  />
                </div>
              </>
            ) : (
              <p>No videos available</p>
            )}
          </div>

          {/* Delete Confirmation Modal */}
          <Modal
            title={`Delete ${
              videoToDelete?.status === "draft" ? "Draft" : "Video"
            }`}
            open={showModal}
            onCancel={() => setShowModal(false)}
            footer={[
              <Button key="delete" type="primary" danger onClick={handleDelete}>
                Yes, Delete
              </Button>,
              <Button key="cancel" onClick={() => setShowModal(false)}>
                Cancel
              </Button>,
            ]}
          >
            <p>
              Are you sure you want to delete{" "}
              {videoToDelete?.status === "draft" ? "this draft" : "this video"}?
            </p>
          </Modal>
        </>
      )}
    </div>
  );
};

export default CreatedVideos;
